import { ContractsTable } from './ContractsTable';
import type { ContractDetail, ContractSearchResult } from '../types';

interface SearchResultsDisplayProps {
  /** The search results data from the API */
  data: ContractSearchResult;
  /** Set of currently selected contract numbers */
  selectedContracts: Set<string>;
  /** Handler for contract selection changes */
  onContractSelectionChange: (
    contractNumber: string,
    isSelected: boolean
  ) => void;
  /** Selection status for the "select all" checkbox */
  isAllOrNoneSelected: 'all' | 'none' | 'some';
  /** Array of contracts to display (may be filtered) */
  displayedContracts: ContractDetail[];
  /** Handler for viewing contract details */
  onViewDetails?: (contract: ContractDetail) => void;
}

/**
 * Component for displaying contract search results.
 *
 * Handles different search result states and renders the appropriate UI:
 * - Success: Shows contracts table with search results
 * - Ambiguous: Shows warning message for ambiguous searches
 * - No results: Shows appropriate empty state message
 *
 * @param props Configuration object for search results display
 */
export function SearchResultsDisplay({
  data,
  selectedContracts,
  onContractSelectionChange,
  isAllOrNoneSelected,
  displayedContracts,
  onViewDetails,
}: SearchResultsDisplayProps) {
  if (data.status === 'success') {
    if (data.contracts.length === 0) {
      return <p className='mt-6'>No contracts found matching your criteria.</p>;
    }

    return (
      <div className='mt-6'>
        <h3 className='text-xl font-semibold mb-3'>Contracts Found:</h3>
        <ContractsTable
          contracts={displayedContracts}
          selectedContracts={selectedContracts}
          onContractSelectionChange={onContractSelectionChange}
          isAllOrNoneSelected={isAllOrNoneSelected}
          onViewDetails={onViewDetails}
        />
      </div>
    );
  }

  if (data.status === 'ambiguous') {
    return <p className='mt-6 text-warning'>{data.message}</p>;
  }

  return null;
}
